/**
 * <TutorLiveActivityList> — les activités de l'élève regardé, pendant le live du masteur (molecule).
 *
 * Le réflexe d'aujourd'hui (`NewStudentsListItem`, la colonne du focus) : la liste d'UN élève, toujours
 * ouverte, sans menu ni onglet (Wajih, 02/11 : « elle doit être facilement accessible ») :
 *  - « Faites aujourd'hui · n », repliable (son chevron), chaque activité et sa note ; cinq au plus,
 *    puis « Voir plus » sur place (`onSeeMoreDone`) ;
 *  - « À faire · n », qu'on réordonne en glissant (`onReorder`, le moteur de « À faire » de l'élève :
 *    la poignée au web, l'appui long au natif).
 * Toucher une ligne l'ouvre au centre ; la ligne qu'on regarde au centre est teintée (`openActivityId`).
 *
 * Round 3 (Wajih) : **le visage de l'élève se pose sur l'activité qu'il a devant lui** (`onScreen`),
 * comme l'ancienne liste, à la place de « en cours » — une activité faite aussi, qu'il relit — avec le
 * temps qu'il y a passé. **Chaque ligne porte la couleur de sa matière**, en touche légère : une teinte à
 * peine posée, et le liseré sur la seule ligne où l'élève est (round 4) — l'ancienne liste la peignait
 * pleine, en bordure et en barre. « Ajouter une
 * activité » et « Son profil » ont quitté la liste pour le centre (le ⋮ et le visage de sa ligne de tête) ;
 * au téléphone, l'onglet Activités les garde (`onAddActivity`, `onOpenProfile`).
 *
 * Deux tailles : `sm` sous sa vidéo dans la pellicule, `lg` dans l'onglet Activités du téléphone. Le
 * dépliage de « Faites aujourd'hui » est un état d'interface (`<Accordion>`) : `defaultDoneOpen` dit où
 * il part. La liste ne trie rien : l'ordre arrive avec l'élève.
 *
 * Daily du 04/11 (Wajih) : la liste toujours dépliée prenait toute la colonne et cachait les autres élèves.
 * `condensed` (la pellicule) ne montre que **l'activité en cours et celle qu'il aura juste après** s'il rend
 * celle-ci (la suivante de « À faire ») ; le reste derrière « Voir tout », un état d'interface. Partout, **seule
 * l'activité en cours est en gras**, et une activité qui s'ouvre par un quiz dit, discrètement, où l'élève en
 * est : le quiz ou l'exercice (`part`).
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { GestureDetector } from "react-native-gesture-handler"
import { StyleSheet } from "react-native-unistyles"

import { activityDurationLabel } from "../../../ports/primary/ui/formatters/activityFormatter"
import { type UITutorLiveActivity } from "../../../ports/primary/ui/types/tutorLive"
import { Avatar } from "../../atoms/Avatar"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { moveItem } from "../../templates/ActivitiesSection/todoReorder"
import { REORDER_BY_HANDLE, useTodoReorderGesture } from "../../templates/ActivitiesSection/useTodoReorderGesture"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Accordion } from "../Accordion"
import { SUBJECT_RAIL_WIDTH, subjectTint } from "../../utils/subjectTouch"
import { LIVE_ACTIVITY_PART, tutorLiveCurrentAndNext } from "./tutorLiveCurrentAndNext"

export type TutorLiveActivityListProps = {
    activities: UITutorLiveActivity[]
    /** L'activité ouverte au centre : sa ligne est teintée. */
    openActivityId?: string
    onOpenActivity: (activityId: string) => void
    /** L'élève : son visage sur la ligne qu'il a devant lui. */
    student: { firstname: string; avatarUrl?: string }
    /** « Ajouter une activité » sous la liste (le téléphone). Absent : le centre le porte. */
    onAddActivity?: () => void
    /** « Son profil » sous la liste (le téléphone). Absent : le visage de la ligne du centre l'ouvre. */
    onOpenProfile?: () => void
    /** Réordonner « À faire » en glissant : l'ordre final. Absent : pas de glisser. */
    onReorder?: (activityIds: string[]) => void
    /** « Voir plus » sous les cinq dernières faites. Absent : toutes, sans lien. */
    onSeeMoreDone?: () => void
    /**
     * D'autres faites attendent derrière « Voir plus », même s'il y en a cinq ou moins ici (l'ancienne liste n'en
     * montrait que celles du jour et les deux d'avant). Défaut `false` : « Voir plus » au-delà de cinq seulement.
     */
    moreDone?: boolean
    /** `sm` (défaut) : sous sa vidéo ; `lg` : l'onglet Activités du téléphone. */
    size?: "sm" | "lg"
    /** « Faites aujourd'hui » part ouvert. Défaut `false`. */
    defaultDoneOpen?: boolean
    /**
     * L'activité en cours et la suivante seulement, le reste derrière « Voir tout » (la pellicule, daily du
     * 04/11). Défaut `false` : toute la liste (l'onglet Activités du téléphone).
     */
    condensed?: boolean
    testID?: string
}

const TEXTS = {
    done: (count: number) => `Faites aujourd'hui · ${count}`,
    todo: (count: number) => `À faire · ${count}`,
    empty: "Aucune activité pour l'instant.",
    add: "Ajouter une activité",
    profile: "Son profil",
    seeMore: "Voir plus",
    onScreen: (firstname: string) => `${firstname} y est en ce moment`,
    /** Le chrono de la ligne : la lecture de partout (« 12 / 20 min », « 20 min » pas ouverte), sinon le seul temps passé. */
    chrono: (activity: UITutorLiveActivity) =>
        activity.estimatedMinutes != null && activity.estimatedMinutes !== 0
            ? activityDurationLabel({ spentMinutes: activity.minutes, estimatedMinutes: activity.estimatedMinutes })
            : activity.minutes != null
              ? `${activity.minutes} min`
              : undefined,
    media: (kind: "photo" | "audio", count: number) =>
        `${count} ${kind === "photo" ? "photo" : "audio"}${count > 1 ? "s" : ""} déposé${count > 1 ? "s" : ""}`,
    review: { validated: "Validée", rejected: "Rejetée", pending: "En attente de correction" },
    partLabel: { quiz: "étape : le quiz", exercise: "étape : l'exercice" },
    seeAll: (count: number) => `Voir tout · ${count}`,
    collapse: "Réduire",
    nothingCurrent: "Rien en cours.",
}

/** Le statut de rendu d'une faite (l'ancien `DoneChip`) : son icône et sa couleur. */
const REVIEW_MARK = {
    validated: { icon: "check-circle", color: "success" },
    rejected: { icon: "times-circle", color: "danger" },
    pending: { icon: "hourglass-half", color: "text-muted" },
} as const

/** Cinq faites au plus avant « Voir plus » (la liste de l'ancienne colonne). */
const DONE_SHOWN = 5

/**
 * Ce que chaque taille change. Une ligne, dans la pellicule, fait 28 px, sous la cible de 44 du livre : le
 * prix d'une liste qui tient sans défiler sous la vidéo (dessin validé le 02/11, `DECISIONS.md`). Au
 * téléphone, 48, une ligne pleine largeur et son filet.
 */
const SIZES = {
    sm: {
        rowHeight: 28,
        title: "text-sm",
        status: "text-xs",
        inset: "2",
        link: "sm",
        linkGap: "3",
        face: 18,
        compact: true,
    },
    lg: {
        rowHeight: 48,
        title: "text-lg",
        status: "text-md",
        inset: "3",
        link: "md",
        linkGap: "6",
        face: 28,
        compact: false,
    },
} as const

type Spec = (typeof SIZES)[keyof typeof SIZES]

export function TutorLiveActivityList(props: TutorLiveActivityListProps) {
    const {
        activities,
        openActivityId,
        onOpenActivity,
        student,
        onAddActivity,
        onOpenProfile,
        onReorder,
        onSeeMoreDone,
        moreDone = false,
        size = "sm",
        defaultDoneOpen = false,
        condensed = false,
        testID,
    } = props
    const spec = SIZES[size]
    // État d'interface : « Voir tout » déplié dans la liste condensée.
    const [showAll, setShowAll] = useState(false)
    const { current: currentActivity, next } = tutorLiveCurrentAndNext(activities)
    const done = activities.filter((activity) => activity.status === "done")
    const shownDone = onSeeMoreDone != null ? done.slice(-DONE_SHOWN) : done
    const todo = activities.filter((activity) => activity.status !== "done")
    const todoIds = todo.map((activity) => activity.id)
    const drag = useTodoReorderGesture(todoIds, onReorder != null ? (ids) => onReorder(ids.map(String)) : undefined)

    // Pendant un glisser, la ligne déplacée prend déjà sa place d'arrivée, soulevée.
    const moving = drag.drag
    const shownTodo =
        moving == null
            ? todo
            : moveItem(
                  todo,
                  todo.findIndex((activity) => activity.id === moving.activityId),
                  moving.toIndex
              )

    /** Une ligne ; `reorderable` : dans « À faire » dépliée, la seule à se glisser (la liste condensée ne se réordonne pas). */
    const row = (activity: UITutorLiveActivity, reorderable: boolean) => {
        const draggable = reorderable && activity.status !== "done" && drag.enabled
        const current = activity.id === currentActivity?.id
        const open = activity.id === openActivityId
        const onScreen = activity.onScreen === true
        const lifted = moving?.activityId === activity.id
        const chrono = TEXTS.chrono(activity)
        const words = [
            activity.title,
            activity.score,
            onScreen ? TEXTS.onScreen(student.firstname) : undefined,
            onScreen ? chrono : undefined,
            activity.chapter,
            activity.part != null ? TEXTS.partLabel[activity.part] : undefined,
            activity.media != null ? TEXTS.media(activity.media.kind, activity.media.count) : undefined,
            activity.finishedLabel,
            activity.reviewStatus != null ? TEXTS.review[activity.reviewStatus] : undefined,
        ].filter(Boolean)
        return (
            <Pressable
                key={activity.id}
                onPress={() => onOpenActivity(activity.id)}
                accessibilityRole="button"
                accessibilityState={{ selected: open }}
                accessibilityLabel={words.join(", ")}
                onLayout={draggable ? (event) => drag.measure(activity.id, event) : undefined}
                style={({ pressed }) => [
                    styles.row(spec, open, activity.subject?.color, lifted, onScreen),
                    pressed && styles.pressed,
                ]}
            >
                {draggable && REORDER_BY_HANDLE && <Icon name="grip-vertical" size={14} color="text-muted" />}
                {onScreen && <Avatar uri={student.avatarUrl} name={student.firstname} size={spec.face} />}
                <Text
                    size={spec.title}
                    weight={current ? "bold" : "regular"}
                    color={activity.status === "done" ? "text-muted" : "text-strong"}
                    numberOfLines={1}
                    style={styles.title}
                >
                    {activity.title}
                </Text>
                {activity.part != null && (
                    <Text size="text-xs" color="text-muted" numberOfLines={1} style={styles.fixed}>
                        {LIVE_ACTIVITY_PART[activity.part]}
                    </Text>
                )}
                {onScreen && chrono != null && (
                    <Text size={spec.status} color="text-muted" numberOfLines={1} style={styles.fixed}>
                        {chrono}
                    </Text>
                )}
                {activity.media != null && activity.media.count > 0 && (
                    <View style={styles.media}>
                        <Icon
                            name={activity.media.kind === "photo" ? "camera" : "microphone"}
                            size={12}
                            color="primary"
                        />
                        <Text size={spec.status} weight="bold" color="primary">
                            {activity.media.count}
                        </Text>
                    </View>
                )}
                {activity.status === "done" && activity.reviewStatus != null && (
                    <Icon
                        name={REVIEW_MARK[activity.reviewStatus].icon}
                        size={12}
                        color={REVIEW_MARK[activity.reviewStatus].color}
                    />
                )}
                {activity.status === "done" && activity.score != null && (
                    <Text size={spec.status} weight="bold" color="text-muted">
                        {activity.score}
                    </Text>
                )}
            </Pressable>
        )
    }
    const todoRows = <View>{shownTodo.map((activity) => row(activity, true))}</View>
    const condensedRows = [currentActivity, next].filter(
        (activity): activity is UITutorLiveActivity => activity != null
    )
    const folded = condensed && !showAll && activities.length > 0

    return (
        <View style={styles.root} testID={testID}>
            {folded ? (
                <>
                    {condensedRows.length > 0 ? (
                        condensedRows.map((activity) => row(activity, false))
                    ) : (
                        <Text size={spec.title} color="text-muted" style={styles.empty(spec)}>
                            {TEXTS.nothingCurrent}
                        </Text>
                    )}
                    {activities.length > condensedRows.length && (
                        <View style={styles.seeMore(spec)}>
                            <TextButton
                                label={TEXTS.seeAll(activities.length)}
                                size={spec.link}
                                onPress={() => setShowAll(true)}
                            />
                        </View>
                    )}
                </>
            ) : activities.length === 0 ? (
                <Text size={spec.title} color="text-muted" style={styles.empty(spec)}>
                    {TEXTS.empty}
                </Text>
            ) : (
                <>
                    {done.length > 0 && (
                        <Accordion
                            defaultExpanded={defaultDoneOpen}
                            header={(open) => (
                                <View style={styles.group(spec)}>
                                    <Text size="text-xs" weight="bold" color="text-muted">
                                        {TEXTS.done(done.length)}
                                    </Text>
                                    <Icon name={open ? "chevron-up" : "chevron-down"} size={16} color="text-muted" />
                                </View>
                            )}
                        >
                            {shownDone.map((activity) => row(activity, false))}
                            {onSeeMoreDone != null && (done.length > DONE_SHOWN || moreDone) && (
                                <View style={styles.seeMore(spec)}>
                                    <TextButton label={TEXTS.seeMore} size={spec.link} onPress={onSeeMoreDone} />
                                </View>
                            )}
                        </Accordion>
                    )}
                    <View style={styles.group(spec)}>
                        <Text size="text-xs" weight="bold" color="text-muted">
                            {TEXTS.todo(todo.length)}
                        </Text>
                    </View>
                    {drag.enabled ? <GestureDetector gesture={drag.gesture}>{todoRows}</GestureDetector> : todoRows}
                    {condensed && (
                        <View style={styles.seeMore(spec)}>
                            <TextButton label={TEXTS.collapse} size={spec.link} onPress={() => setShowAll(false)} />
                        </View>
                    )}
                </>
            )}
            {(onAddActivity != null || onOpenProfile != null) && (
                <View style={styles.links(spec)}>
                    {onAddActivity != null && <TextButton label={TEXTS.add} size={spec.link} onPress={onAddActivity} />}
                    {onOpenProfile != null && (
                        <TextButton label={TEXTS.profile} size={spec.link} onPress={onOpenProfile} />
                    )}
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { gap: 1, minWidth: 0 },
    /** Les photos ou audios déjà déposés : l'icône et le compte, serrés (l'ancien `ActivityInfosIcons`). */
    media: { flexDirection: "row" as const, alignItems: "center" as const, gap: 2 },
    group: (spec: Spec) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["1"],
        minHeight: 32,
        paddingHorizontal: theme.spacing[spec.inset],
    }),
    /**
     * Une ligne : la touche de sa matière (sa teinte), plus franche sur la ligne ouverte au
     * centre ; la ligne où l'élève est en ce moment prend le liseré de sa matière ; soulevée pendant
     * qu'on la glisse.
     */
    row: (spec: Spec, open: boolean, subject: SemanticColorKey | undefined, lifted: boolean, current: boolean) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing[spec.compact ? "1" : "2"],
        minHeight: spec.rowHeight,
        paddingLeft: theme.spacing[spec.inset],
        paddingRight: theme.spacing[spec.inset],
        paddingVertical: spec.compact ? 2 : theme.spacing["1"],
        borderRadius: spec.compact ? theme.radius.sm : 0,
        borderBottomWidth: spec.compact ? 0 : 1,
        borderBottomColor: theme.colors["border-subtle"],
        borderLeftWidth: current && subject != null ? SUBJECT_RAIL_WIDTH : 0,
        borderLeftColor: current && subject != null ? theme.colors[subject] : "transparent",
        backgroundColor: open
            ? theme.colors["surface-subtle"]
            : subject != null
              ? subjectTint(theme.colors[subject])
              : "transparent",
        ...(lifted ? { borderWidth: 1, borderColor: theme.colors.role } : {}),
    }),
    title: { flexShrink: 1, minWidth: 0 },
    fixed: { flexShrink: 0 },
    pressed: { opacity: 0.7 },
    empty: (spec: Spec) => ({ paddingHorizontal: theme.spacing[spec.inset], paddingVertical: theme.spacing["1"] }),
    seeMore: (spec: Spec) => ({ paddingHorizontal: theme.spacing[spec.inset], paddingVertical: 2 }),
    links: (spec: Spec) => ({
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        columnGap: theme.spacing[spec.linkGap],
        rowGap: theme.spacing["1"],
        paddingHorizontal: theme.spacing[spec.inset],
        paddingTop: theme.spacing[spec.compact ? "1" : "3"],
    }),
}))
